import React, { Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Layout } from '@/components/Layout';
import { isPublicPath, routes } from '@/routes';
import LoadingSpinner from '@/components/common/ui/LoadingSpinner';
import { checkAuth, logoutUser } from '@/store/slices/authSlice';
import { RootState, AppDispatch } from '@/store';

// Loading component for code-split routes
const LoadingFallback = () => <LoadingSpinner fullScreen />;

const publicRoutes = routes.filter(r => r.isPublic);
const appRoutes = routes.filter(r => !r.isPublic);

type AuthEventType = 'unauthorized' | 'forbidden' | 'auth_error';

const AUTH_EVENT_NAME = 'measure-ease:auth-event';
const SESSION_EXPIRED_FLAG = 'auth:session-expired';

function redirectTo(path: '/login' | '/unauthorized') {
  if (typeof window === 'undefined') return;
  if (window.location.pathname === path) return;
  window.location.replace(path);
}

function markSessionExpired() {
  try {
    sessionStorage.setItem(SESSION_EXPIRED_FLAG, '1');
  } catch {
    // ignore
  }
}

function handleAuthEvent(type: AuthEventType, dispatch: AppDispatch) {
  switch (type) {
    case 'unauthorized': {
      dispatch(logoutUser());
      if (isPublicPath(window.location.pathname)) return;
      markSessionExpired();
      redirectTo('/login');
      return;
    }
    case 'forbidden': {
      redirectTo('/unauthorized');
      return;
    }
    default: {
      return;
    }
  }
}

function App() {
  const dispatch: AppDispatch = useDispatch();
  const { isAuthCheckLoading } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    dispatch(checkAuth());
  }, [dispatch]);

  useEffect(() => {
    const handler = (event: Event) => {
      const custom = event as CustomEvent<{ type?: AuthEventType }>;
      const type = custom.detail?.type;
      if (!type) return;
      handleAuthEvent(type, dispatch);
    };

    window.addEventListener(AUTH_EVENT_NAME, handler as EventListener);
    return () => {
      window.removeEventListener(AUTH_EVENT_NAME, handler as EventListener);
    };
  }, [dispatch]);

  // Show a global loader while checking for an existing session
  if (isAuthCheckLoading) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <BrowserRouter>
      <Routes>
        {publicRoutes.map(route => (
          <Route
            key={route.path}
            path={route.path}
            element={<Suspense fallback={<LoadingFallback />}>{route.element}</Suspense>}
          />
        ))}

        {/* Protected application routes rendered within the main Layout */}
        <Route path="/" element={<Layout />}>
          {appRoutes.map((route, idx) => {
            if (route.children) {
              return (
                <Route
                  key={route.path || `index-${idx}`}
                  path={route.path}
                  element={<Suspense fallback={<LoadingFallback />}>{route.element}</Suspense>}
                >
                  {route.children.map((child, childIdx) => (
                    <Route
                      key={child.path || `child-${childIdx}`}
                      index={child.index}
                      path={child.path}
                      element={<Suspense fallback={<LoadingFallback />}>{child.element}</Suspense>}
                    />
                  ))}
                </Route>
              );
            }
            return (
              <Route
                key={route.path || `index-${idx}`}
                index={route.index}
                path={route.path}
                element={<Suspense fallback={<LoadingFallback />}>{route.element}</Suspense>}
              />
            );
          })}
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
